import React from 'react';
import { createPortal } from 'react-dom';
import { User, PencilRuler, PenTool, HardHat, MapPin } from 'lucide-react';
import type { EORRecord } from '../../types';

export type ProjectMetaHoverState = {
  x: number;
  y: number;
};

type EngineeringFirm = {
  id: string;
  name: string;
  engineers: Array<{ id: string; name: string; email?: string }>;
};

type DesignerOption = { uid: string; displayName: string };

export type ProjectHubMetaFlyoutProps = {
  hover: ProjectMetaHoverState | null;
  /** Build site / project location for install weather. */
  siteAddress?: string;
  client: string;
  designerUid: string;
  designerName: string;
  designersList: DesignerOption[];
  architectName: string;
  architectDraft: string;
  onArchitectDraftChange: (value: string) => void;
  onArchitectBlur: () => void;
  onArchitectEscape: () => void;
  eor: EORRecord | null;
  engineeringFirms: EngineeringFirm[];
  eorFirmDraft: string;
  onEorFirmDraftChange: (firmId: string) => void;
  onSelectEngineer: (firmId: string, engineerId: string) => void;
  canEditDealMeta: boolean;
  canEditEngineer: boolean;
  onDesignerChange: (uid: string) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

function clampFlyoutPosition(x: number, y: number, flyoutW: number, reserveH: number): { left: number; top: number } {
  const pad = 10;
  const margin = 14;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 800;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 600;
  const h = Math.min(reserveH, Math.max(120, vh - pad * 2 - margin * 2));

  let left = x + margin;
  if (left + flyoutW + pad > vw) left = Math.max(pad, vw - flyoutW - pad);

  let top = y + margin;
  if (top + h + pad > vh) top = y - h - margin;
  if (top < pad) top = pad;
  if (top + h + pad > vh) top = Math.max(pad, vh - h - pad);

  return { left, top };
}

export function ProjectHubMetaFlyout({
  hover,
  siteAddress = '',
  client,
  designerUid,
  designerName,
  designersList,
  architectName,
  architectDraft,
  onArchitectDraftChange,
  onArchitectBlur,
  onArchitectEscape,
  eor,
  engineeringFirms,
  eorFirmDraft,
  onEorFirmDraftChange,
  onSelectEngineer,
  canEditDealMeta,
  canEditEngineer,
  onDesignerChange,
  onMouseEnter,
  onMouseLeave,
}: ProjectHubMetaFlyoutProps) {
  if (!hover || typeof document === 'undefined') return null;

  const reserveH =
    typeof window !== 'undefined' ? Math.min(280, Math.max(160, Math.floor(window.innerHeight * 0.38))) : 220;
  const { left, top } = clampFlyoutPosition(hover.x, hover.y, 300, reserveH);
  const selectedFirm = engineeringFirms.find(f => f.id === eorFirmDraft);
  const engineerDisplay = eor ? `${eor.engineerName} (${eor.firmName})` : '';

  return createPortal(
    <div
      className="pm-hub-meta-flyout"
      style={{ left, top }}
      role="dialog"
      aria-label="Project contacts"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="pm-hub-meta-flyout-title">Project info</div>

      <div className="pm-hub-meta-flyout-row">
        <span className="pm-hub-meta-flyout-label">
          <MapPin size={12} aria-hidden />
          site
        </span>
        <span className="pm-hub-meta-flyout-value">
          {siteAddress.trim() ? (
            <span className="pm-hub-meta-flyout-text">{siteAddress.trim()}</span>
          ) : (
            <span className="pm-hub-meta-flyout-empty">No address — edit project to add</span>
          )}
        </span>
      </div>

      <div className="pm-hub-meta-flyout-row">
        <span className="pm-hub-meta-flyout-label">
          <User size={12} aria-hidden />
          client
        </span>
        <span className="pm-hub-meta-flyout-value">
          {client ? (
            <span className="pm-hub-meta-flyout-text">{client}</span>
          ) : (
            <span className="pm-hub-meta-flyout-empty">—</span>
          )}
        </span>
      </div>

      <div className="pm-hub-meta-flyout-row">
        <span className="pm-hub-meta-flyout-label">
          <PencilRuler size={12} aria-hidden />
          designer
        </span>
        <span className="pm-hub-meta-flyout-value">
          {canEditDealMeta ? (
            <select
              className="pm-hub-meta-flyout-select"
              value={designerUid}
              onChange={e => onDesignerChange(e.target.value)}
              aria-label="Assigned designer"
            >
              <option value="">—</option>
              {designersList.map(d => (
                <option key={d.uid} value={d.uid}>
                  {d.displayName}
                </option>
              ))}
            </select>
          ) : designerName ? (
            <span className="pm-hub-meta-flyout-text">{designerName}</span>
          ) : (
            <span className="pm-hub-meta-flyout-empty">—</span>
          )}
        </span>
      </div>

      <div className="pm-hub-meta-flyout-row">
        <span className="pm-hub-meta-flyout-label">
          <PenTool size={12} aria-hidden />
          architect
        </span>
        <span className="pm-hub-meta-flyout-value">
          {canEditDealMeta ? (
            <input
              type="text"
              className="pm-hub-meta-flyout-input"
              value={architectDraft}
              onChange={e => onArchitectDraftChange(e.target.value)}
              onBlur={onArchitectBlur}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') {
                  onArchitectEscape();
                  e.currentTarget.blur();
                }
              }}
              placeholder="External firm…"
              aria-label="External architect organization"
            />
          ) : architectName ? (
            <span className="pm-hub-meta-flyout-text">{architectName}</span>
          ) : (
            <span className="pm-hub-meta-flyout-empty">—</span>
          )}
        </span>
      </div>

      <div className="pm-hub-meta-flyout-row">
        <span className="pm-hub-meta-flyout-label">
          <HardHat size={12} aria-hidden />
          engineer
        </span>
        <span className="pm-hub-meta-flyout-value pm-hub-meta-flyout-engineer">
          {canEditEngineer && engineeringFirms.length > 0 ? (
            <>
              <select
                className="pm-hub-meta-flyout-select"
                value={eorFirmDraft}
                onChange={e => onEorFirmDraftChange(e.target.value)}
                aria-label="Engineering firm"
              >
                <option value="">Select firm…</option>
                {engineeringFirms.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              {selectedFirm && (
                selectedFirm.engineers.length === 0 ? (
                  <span className="pm-hub-meta-flyout-empty">No engineers in this firm</span>
                ) : (
                  <select
                    className="pm-hub-meta-flyout-select"
                    value={eor?.firmId === selectedFirm.id ? eor.engineerId : ''}
                    onChange={e => onSelectEngineer(selectedFirm.id, e.target.value)}
                    aria-label="Engineer of record"
                  >
                    <option value="">Select engineer…</option>
                    {selectedFirm.engineers.map(eng => (
                      <option key={eng.id} value={eng.id}>
                        {eng.name}
                      </option>
                    ))}
                  </select>
                )
              )}
            </>
          ) : engineerDisplay ? (
            <span className="pm-hub-meta-flyout-text">{engineerDisplay}</span>
          ) : (
            <span className="pm-hub-meta-flyout-empty">—</span>
          )}
        </span>
      </div>
    </div>,
    document.body,
  );
}
